import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { format } from "date-fns";
import { FileText, Download, Loader2, List, Map } from "lucide-react";

// Helper to generate PDF-ready HTML
const generateBucketListHTML = (data) => {
  const categoryLabels = {
    travel: "Travel",
    adventures: "Adventures",
    experiences: "Experiences",
    goals: "Goals",
    romance: "Romance"
  };

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Bucket List - ${data.user.name}${data.user.partner_name ? ` & ${data.user.partner_name}` : ''}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #264653; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { font-size: 28px; margin-bottom: 8px; color: #E07A5F; }
    h2 { font-size: 20px; margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid #E07A5F; }
    h3 { font-size: 16px; margin: 16px 0 8px; }
    .subtitle { color: #6B7280; font-size: 14px; margin-bottom: 24px; }
    .stats { display: flex; gap: 24px; margin: 24px 0; padding: 16px; background: #F4F1DE; border-radius: 8px; }
    .stat { text-align: center; }
    .stat-value { font-size: 32px; font-weight: bold; color: #E07A5F; }
    .stat-label { font-size: 12px; color: #6B7280; }
    .category { margin: 24px 0; }
    .category-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
    .category-badge { padding: 4px 12px; border-radius: 16px; font-size: 12px; font-weight: 600; }
    .item { padding: 12px 16px; margin: 8px 0; background: #fff; border: 1px solid #EAE7D6; border-radius: 8px; }
    .item-title { font-weight: 600; }
    .item-completed { text-decoration: line-through; opacity: 0.6; }
    .item-meta { font-size: 12px; color: #6B7280; margin-top: 4px; }
    .item-desc { font-size: 14px; color: #6B7280; margin-top: 4px; }
    .checkmark { color: #81B29A; margin-right: 8px; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #EAE7D6; font-size: 12px; color: #6B7280; text-align: center; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <h1>Our Bucket List</h1>
  <p class="subtitle">${data.user.name}${data.user.partner_name ? ` & ${data.user.partner_name}` : ''} • Generated ${format(new Date(data.generated_at), 'MMMM d, yyyy')}</p>
  
  <div class="stats">
    <div class="stat">
      <div class="stat-value">${data.stats.total}</div>
      <div class="stat-label">Total Dreams</div>
    </div>
    <div class="stat">
      <div class="stat-value">${data.stats.completed}</div>
      <div class="stat-label">Completed</div>
    </div>
    <div class="stat">
      <div class="stat-value">${data.stats.percentage}%</div>
      <div class="stat-label">Progress</div>
    </div>
  </div>

  ${Object.entries(data.by_category).map(([cat, catData]) => `
    <div class="category">
      <div class="category-header">
        <h2>${categoryLabels[cat] || cat}</h2>
        <span style="font-size: 14px; color: #6B7280;">(${catData.completed}/${catData.total} completed)</span>
      </div>
      ${catData.items.map(item => `
        <div class="item">
          <div class="item-title ${item.completed ? 'item-completed' : ''}">
            ${item.completed ? '<span class="checkmark">✓</span>' : '○ '}${item.title}
          </div>
          ${item.description ? `<div class="item-desc">${item.description}</div>` : ''}
          <div class="item-meta">
            ${item.target_date ? `Target: ${format(new Date(item.target_date), 'MMM d, yyyy')} • ` : ''}
            Priority: ${item.priority} • By ${item.created_by_name}
          </div>
        </div>
      `).join('')}
    </div>
  `).join('')}

  <div class="footer">
    Created with DuoLyst - Share your dreams together
  </div>
</body>
</html>
  `;
};

const generateTripSummaryHTML = (data) => {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Trip Summary - ${data.item.title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #264653; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { font-size: 28px; margin-bottom: 8px; color: #E07A5F; }
    h2 { font-size: 18px; margin: 24px 0 12px; padding-bottom: 8px; border-bottom: 2px solid #E07A5F; }
    .subtitle { color: #6B7280; font-size: 14px; margin-bottom: 8px; }
    .description { color: #6B7280; margin-bottom: 24px; }
    .stats { display: flex; gap: 16px; margin: 24px 0; }
    .stat { padding: 12px 20px; background: #F4F1DE; border-radius: 8px; text-align: center; }
    .stat-value { font-size: 24px; font-weight: bold; color: #E07A5F; }
    .stat-label { font-size: 11px; color: #6B7280; }
    .activity { padding: 12px 16px; margin: 8px 0; background: #fff; border: 1px solid #EAE7D6; border-radius: 8px; }
    .activity-title { font-weight: 600; }
    .activity-completed { text-decoration: line-through; opacity: 0.6; }
    .activity-meta { font-size: 12px; color: #6B7280; margin-top: 4px; }
    .comment { padding: 12px; margin: 8px 0; background: #F4F1DE; border-radius: 8px; }
    .comment-text { font-size: 14px; }
    .comment-meta { font-size: 11px; color: #6B7280; margin-top: 4px; }
    .notes { padding: 16px; background: #FEF3C7; border-radius: 8px; margin-top: 24px; }
    .notes-title { font-weight: 600; font-size: 14px; margin-bottom: 8px; }
    .checkmark { color: #81B29A; margin-right: 8px; }
    .footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #EAE7D6; font-size: 12px; color: #6B7280; text-align: center; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <h1>${data.item.title}</h1>
  <p class="subtitle">By ${data.item.created_by_name}${data.item.target_date ? ` • Target: ${format(new Date(data.item.target_date), 'MMMM d, yyyy')}` : ''}</p>
  ${data.item.description ? `<p class="description">${data.item.description}</p>` : ''}
  
  <div class="stats">
    <div class="stat">
      <div class="stat-value">${data.itinerary_stats.total}</div>
      <div class="stat-label">Activities</div>
    </div>
    <div class="stat">
      <div class="stat-value">${data.itinerary_stats.completed}</div>
      <div class="stat-label">Completed</div>
    </div>
    <div class="stat">
      <div class="stat-value">${data.photo_count}</div>
      <div class="stat-label">Photos</div>
    </div>
    <div class="stat">
      <div class="stat-value">${data.comment_count}</div>
      <div class="stat-label">Comments</div>
    </div>
  </div>

  ${data.itinerary.length > 0 ? `
    <h2>Itinerary</h2>
    ${data.itinerary.map(activity => `
      <div class="activity">
        <div class="activity-title ${activity.completed ? 'activity-completed' : ''}">
          ${activity.completed ? '<span class="checkmark">✓</span>' : '○ '}${activity.title}
        </div>
        <div class="activity-meta">
          ${activity.date ? format(new Date(activity.date), 'MMM d') : ''}
          ${activity.time ? ` at ${activity.time}` : ''}
          ${activity.location ? ` • ${activity.location}` : ''}
        </div>
        ${activity.notes ? `<div class="activity-meta" style="margin-top: 4px;">${activity.notes}</div>` : ''}
      </div>
    `).join('')}
  ` : ''}

  ${data.comments.length > 0 ? `
    <h2>Discussion</h2>
    ${data.comments.map(comment => `
      <div class="comment">
        <div class="comment-text">${comment.text}</div>
        <div class="comment-meta">${comment.created_by_name} • ${format(new Date(comment.created_at), 'MMM d, yyyy')}</div>
      </div>
    `).join('')}
  ` : ''}

  ${data.item.notes ? `
    <div class="notes">
      <div class="notes-title">Notes</div>
      <p>${data.item.notes}</p>
    </div>
  ` : ''}

  <div class="footer">
    Generated ${format(new Date(data.generated_at), 'MMMM d, yyyy')} • Created with DuoLyst
  </div>
</body>
</html>
  `;
};

export const ExportButton = ({ type = "bucket-list", itemId = null }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      let data, html, filename;
      
      if (type === "bucket-list") {
        const res = await axios.get(`${API}/export/bucket-list`);
        data = res.data;
        html = generateBucketListHTML(data);
        filename = `bucket-list-${format(new Date(), 'yyyy-MM-dd')}.html`;
      } else if (type === "trip" && itemId) {
        const res = await axios.get(`${API}/export/trip/${itemId}`);
        data = res.data;
        html = generateTripSummaryHTML(data);
        filename = `trip-${data.item.title.toLowerCase().replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd')}.html`;
      }
      
      // Create and download file
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Export ready! Open in browser and print to PDF");
    } catch (error) {
      toast.error("Failed to export");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={handleExport}
      disabled={loading}
      className="rounded-lg border-charcoal/10"
      data-testid={`export-${type}-btn`}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <Download className="w-4 h-4 mr-2" />
      )}
      {type === "bucket-list" ? "Export List" : "Export Trip"}
    </Button>
  );
};

export const ExportModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card border-none rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Export Options
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Download your bucket list for printing or sharing
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <div className="p-4 bg-background rounded-xl border border-charcoal/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <List className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-charcoal">Full Bucket List</h3>
                <p className="text-xs text-muted-foreground">All items organized by category</p>
              </div>
            </div>
            <ExportButton type="bucket-list" />
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Tip: Open the downloaded file in your browser and use Print → Save as PDF
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportButton;
